import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AlertTriangle,
  Package,
  DollarSign,
  RefreshCw,
  FileText,
  FileSpreadsheet,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { supabase } from '../../lib/supabase';
import {
  type MissingOrderAnalysisRow,
  type MissingOrderAnalysisAggregatedRow,
  aggregateMissingOrdersAnalysis,
  exportMissingOrdersAnalysisToPDF,
  exportMissingOrdersAnalysisToExcel,
} from '../../utils/reportExport';

const PAGE_SIZES = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;
/** Must be ≤ PostgREST db-max-rows (typically 1000) to avoid response truncation. */
const ANALYSIS_PAGE_SIZE = 1000;
type SortField =
  | 'order_number'
  | 'order_date'
  | 'customer_name'
  | 'missing_count'
  | 'total_to_refund'
  | 'total_order_price';

export interface MissingOrderAnalysisReportProps {
  adminId: string | null;
  user: {
    id?: string;
    company_name?: string;
    company_address?: string;
    company_phone?: string;
    email?: string;
  } | null;
}

export const MissingOrderAnalysisReport: React.FC<MissingOrderAnalysisReportProps> = ({
  adminId,
  user,
}) => {
  const [rawRows, setRawRows] = useState<MissingOrderAnalysisRow[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('order_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(0);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const fetchData = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    setError(null);
    try {
      const countPromise = supabase.rpc('count_orders_with_missing_items', { p_admin_id: adminId });
      const allRows: MissingOrderAnalysisRow[] = [];
      let offset = 0;
      let chunk: MissingOrderAnalysisRow[];

      do {
        const res = await supabase.rpc('get_missing_orders_analysis', {
          p_admin_id: adminId,
          p_limit: ANALYSIS_PAGE_SIZE,
          p_offset: offset,
        });
        if (res.error) throw res.error;
        chunk = (res.data ?? []) as MissingOrderAnalysisRow[];
        allRows.push(...chunk);
        offset += ANALYSIS_PAGE_SIZE;
      } while (chunk.length === ANALYSIS_PAGE_SIZE);

      const countRes = await countPromise;
      if (countRes.error) throw countRes.error;
      setCount(countRes.data != null ? Number(countRes.data) : null);
      setRawRows(allRows);
      setLastUpdated(new Date());
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load missing order analysis';
      setError(message);
      setRawRows([]);
      setCount(null);
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    if (adminId) fetchData();
  }, [adminId, fetchData]);

  const aggregated = useMemo(
    () => aggregateMissingOrdersAnalysis(rawRows),
    [rawRows]
  );

  const totalLineItems = useMemo(
    () => rawRows.reduce((sum, r) => sum + Number(r.missing_quantity ?? 0), 0),
    [rawRows]
  );

  const totalToRefund = useMemo(
    () => rawRows.reduce((sum, r) => sum + Number(r.missing_line_total ?? 0), 0),
    [rawRows]
  );

  const displayCount = count ?? aggregated.length;
  const currency = rawRows[0]?.currency ?? '';

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return aggregated;
    const q = searchTerm.toLowerCase().trim();
    return aggregated.filter(
      (row) =>
        (row.order_number ?? '').toLowerCase().includes(q) ||
        (row.customer_name ?? '').toLowerCase().includes(q) ||
        (row.customer_email ?? '').toLowerCase().includes(q) ||
        (row.customer_phone ?? '').toLowerCase().includes(q) ||
        (row.missing_skus ?? '').toLowerCase().includes(q) ||
        (row.missing_titles ?? '').toLowerCase().includes(q) ||
        (row.shipping_address ?? '').toLowerCase().includes(q)
    );
  }, [aggregated, searchTerm]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (sortField) {
        case 'order_number':
          aVal = a.order_number ?? '';
          bVal = b.order_number ?? '';
          break;
        case 'order_date':
          aVal = a.order_date ? new Date(a.order_date).getTime() : 0;
          bVal = b.order_date ? new Date(b.order_date).getTime() : 0;
          break;
        case 'customer_name':
          aVal = (a.customer_name ?? '').toLowerCase();
          bVal = (b.customer_name ?? '').toLowerCase();
          break;
        case 'missing_count':
          aVal = a.missing_count;
          bVal = b.missing_count;
          break;
        case 'total_to_refund':
          aVal = a.total_to_refund;
          bVal = b.total_to_refund;
          break;
        case 'total_order_price':
          aVal = a.total_order_price;
          bVal = b.total_order_price;
          break;
        default:
          aVal = '';
          bVal = '';
      }
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal as string);
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });
    return arr;
  }, [filtered, sortField, sortDirection]);

  const totalFiltered = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const page = Math.min(currentPage, totalPages - 1);
  const paginated = useMemo(
    () => sorted.slice(page * pageSize, page * pageSize + pageSize),
    [sorted, page, pageSize]
  );
  const from = totalFiltered === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, totalFiltered);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(0);
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4 ml-0.5 inline-block text-amber-600 dark:text-amber-400" aria-hidden />
    ) : (
      <ChevronDown className="h-4 w-4 ml-0.5 inline-block text-amber-600 dark:text-amber-400" aria-hidden />
    );
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const { data: settings } = await supabase
        .from('user_settings')
        .select('avatar_data')
        .eq('user_id', user?.id)
        .maybeSingle();
      exportMissingOrdersAnalysisToPDF(sorted, {
        companyName: user?.company_name,
        companyAddress: user?.company_address,
        companyPhone: user?.company_phone,
        companyEmail: user?.email,
        companyLogo: settings?.avatar_data,
      });
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const { data: settings } = await supabase
        .from('user_settings')
        .select('avatar_data')
        .eq('user_id', user?.id)
        .maybeSingle();
      await exportMissingOrdersAnalysisToExcel(sorted, {
        companyName: user?.company_name,
        companyAddress: user?.company_address,
        companyPhone: user?.company_phone,
        companyEmail: user?.email,
        companyLogo: settings?.avatar_data,
      });
    } finally {
      setExportingExcel(false);
    }
  };

  if (!adminId) {
    return (
      <div className="card p-8">
        <p className="text-sm text-gray-500 dark:text-gray-400">You must be signed in to view this report.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card p-8">
        <header className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-200 dark:border-amber-700">
              <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" aria-hidden />
            </span>
            <span>Missing Order Analysis</span>
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Paid, unfulfilled orders where at least one line-item SKU has no product with bin inventory.
          </p>
        </header>

        {error && (
          <div
            className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            role="alert"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" aria-hidden />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">Failed to load report</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
            <Button variant="secondary" onClick={fetchData} disabled={loading} className="flex-shrink-0">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} aria-hidden />
              Retry
            </Button>
          </div>
        )}

        {loading && rawRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16" aria-busy="true" aria-live="polite">
            <Loader2 className="h-12 w-12 animate-spin text-amber-600 dark:text-amber-400 mb-4" aria-hidden />
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Loading missing order analysis…</p>
          </div>
        ) : (
          <>
            {aggregated.length === 0 ? (
              <div
                className="py-16 px-6 text-center rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30"
                role="status"
              >
                <Package className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" aria-hidden />
                <p className="text-lg font-medium text-gray-700 dark:text-gray-300">No orders with missing items</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-md mx-auto">
                  Orders with missing items are paid, unfulfilled orders where at least one line-item SKU has no product with bin inventory.
                </p>
                <Button variant="secondary" onClick={fetchData} disabled={loading} className="mt-6">
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} aria-hidden />
                  Refresh
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  <div className="p-5 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-xl border border-amber-200 dark:border-amber-700">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden />
                      <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                        Orders with missing items
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-amber-900 dark:text-amber-100 tabular-nums">
                      {displayCount.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-5 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-xl border border-red-200 dark:border-red-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden />
                      <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider">
                        Total missing line items
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-red-900 dark:text-red-100 tabular-nums">
                      {totalLineItems.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-5 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/20 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-gray-600 dark:text-gray-400" aria-hidden />
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Total to be refunded
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                      {totalToRefund.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                      {currency}
                    </p>
                  </div>
                </div>

                {lastUpdated && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4" role="status">
                    <Clock className="h-3.5 w-3.5" aria-hidden />
                    <span>Last updated: {lastUpdated.toLocaleString()}</span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <div className="relative flex-1 max-w-sm">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                      aria-hidden
                    />
                    <input
                      type="search"
                      placeholder="Search by order #, customer, email, SKU…"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(0);
                      }}
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 dark:focus:border-amber-500 text-sm"
                      aria-label="Search orders"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="secondary" onClick={fetchData} disabled={loading}>
                      <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} aria-hidden />
                      Refresh
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleExportPdf}
                      disabled={aggregated.length === 0 || exportingPdf}
                      loading={exportingPdf}
                    >
                      <FileText className="h-4 w-4 mr-2" aria-hidden />
                      Download PDF
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleExportExcel}
                      disabled={aggregated.length === 0 || exportingExcel}
                      loading={exportingExcel}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" aria-hidden />
                      Download Excel
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                  <table
                    className="w-full min-w-[900px]"
                    role="table"
                    aria-label="Missing orders analysis"
                    aria-describedby="missing-orders-caption"
                  >
                    <caption id="missing-orders-caption" className="sr-only">
                      One row per order with missing items: order number, date, customer, missing SKUs and items, quantities and refund totals.
                    </caption>
                    <thead className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-750">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          <button
                            type="button"
                            onClick={() => handleSort('order_number')}
                            className="inline-flex items-center hover:text-amber-600 dark:hover:text-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded"
                          >
                            Order #
                            <SortIcon field="order_number" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          <button
                            type="button"
                            onClick={() => handleSort('order_date')}
                            className="inline-flex items-center hover:text-amber-600 dark:hover:text-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded"
                          >
                            Order date
                            <SortIcon field="order_date" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider hidden xl:table-cell">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider hidden 2xl:table-cell">Address</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          <button
                            type="button"
                            onClick={() => handleSort('missing_count')}
                            className="inline-flex items-center hover:text-amber-600 dark:hover:text-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded mx-auto"
                          >
                            # Missing
                            <SortIcon field="missing_count" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Missing SKUs</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Missing items</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Total missing qty</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          <button
                            type="button"
                            onClick={() => handleSort('total_to_refund')}
                            className="inline-flex items-center hover:text-amber-600 dark:hover:text-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded ml-auto"
                          >
                            Total to refund
                            <SortIcon field="total_to_refund" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          <button
                            type="button"
                            onClick={() => handleSort('total_order_price')}
                            className="inline-flex items-center hover:text-amber-600 dark:hover:text-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded ml-auto"
                          >
                            Order total
                            <SortIcon field="total_order_price" />
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {paginated.map((row, index) => (
                        <tr
                          key={row.order_id}
                          className={
                            index % 2 === 0
                              ? 'bg-white dark:bg-gray-900'
                              : 'bg-gray-50/50 dark:bg-gray-800/50'
                          }
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {row.order_number ?? '–'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {row.order_date ? new Date(row.order_date).toLocaleDateString() : '–'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {row.customer_name ?? '–'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hidden xl:table-cell">
                            {row.customer_phone ?? '–'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-[140px] truncate hidden lg:table-cell" title={row.customer_email ?? undefined}>
                            {row.customer_email ?? '–'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[160px] truncate hidden 2xl:table-cell" title={row.shipping_address ?? undefined}>
                            {row.shipping_address ?? '–'}
                          </td>
                          <td className="px-4 py-3 text-sm text-center font-medium text-gray-900 dark:text-gray-100 tabular-nums">
                            {row.missing_count}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-gray-800 dark:text-gray-200 max-w-[180px] truncate" title={row.missing_skus}>
                            {row.missing_skus || '–'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={row.missing_titles}>
                            {row.missing_titles || '–'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 tabular-nums">
                            {row.total_missing_quantity}
                          </td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums font-medium text-gray-900 dark:text-gray-100">
                            {Number(row.total_to_refund).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                            {Number(row.total_order_price).toFixed(2)} {row.currency ?? ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700">
                      <tr>
                        <td colSpan={12} className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-right">
                          Total to be refunded: {totalToRefund.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                          {currency}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {totalFiltered > 0 && (
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>
                        Showing {from}–{to} of {totalFiltered.toLocaleString()}
                        {searchTerm && filtered.length !== aggregated.length && ' (filtered)'}
                      </span>
                      <label className="flex items-center gap-2">
                        <span>Per page</span>
                        <select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(0);
                          }}
                          className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm py-1.5 px-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          aria-label="Rows per page"
                        >
                          {PAGE_SIZES.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                        disabled={page <= 0}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" aria-hidden />
                      </Button>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">
                        Page {page + 1} of {totalPages}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" aria-hidden />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
